/**
 * Receive-side check: wrapped session keys must only name identities in the
 * locally pinned conversation member set (IndexedDB TOFU pin).
 *
 * Send/call already skip wrapping to unpinned members via `evaluatePeerTrust`.
 * A malicious member can still sign extra wraps for an outsider; honest
 * clients must not treat that payload as trusted.
 *
 * No pin yet (first contact / IndexedDB unavailable): skip. That residual
 * TOFU window is accepted until the first successful key fetch pins members.
 */

import { getConversationMemberPin } from './peerKeyPinStorage';

export type WrapIdentityRef = {
  identityId?: string;
  recipientIdentityId?: string;
};

function wrapTargetIdentityId(wrap: WrapIdentityRef): string | undefined {
  const id = wrap.identityId ?? wrap.recipientIdentityId;
  return id ? id : undefined;
}

/**
 * Identity IDs present on wraps but not in the pinned member set.
 * `alwaysAllowed` is the local identity so our own wrap is never treated as
 * an outsider if the pin omitted us.
 */
export function wrapIdentitiesOutsidePinnedMembers(
  wraps: ReadonlyArray<WrapIdentityRef>,
  pinnedIdentityIds: readonly string[],
  alwaysAllowed: readonly string[] = [],
): string[] {
  const allowed = new Set(pinnedIdentityIds);
  for (const id of alwaysAllowed) {
    if (id) allowed.add(id);
  }
  const extra: string[] = [];
  const seen = new Set<string>();
  for (const wrap of wraps) {
    const id = wrapTargetIdentityId(wrap);
    if (!id || allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    extra.push(id);
  }
  return extra;
}

/**
 * True when a pin exists and any wrap names an identity outside it.
 * `pinnedIdentityIds == null` means no pin yet — do not fail closed.
 */
export function wrapsViolatePinnedMemberSet(
  wraps: ReadonlyArray<WrapIdentityRef> | undefined,
  pinnedIdentityIds: readonly string[] | null | undefined,
  alwaysAllowed: readonly string[] = [],
): boolean {
  if (pinnedIdentityIds == null) return false;
  if (!wraps?.length) return false;
  return wrapIdentitiesOutsidePinnedMembers(wraps, pinnedIdentityIds, alwaysAllowed).length > 0;
}

export async function loadPinnedMemberIdentityIds(
  conversationId: string,
): Promise<string[] | null> {
  const pin = await getConversationMemberPin(conversationId);
  return pin?.identityIds ?? null;
}

export async function conversationWrapsViolatePinnedMembers(
  conversationId: string,
  wraps: ReadonlyArray<WrapIdentityRef> | undefined,
  localIdentityId: string,
): Promise<boolean> {
  const pinned = await loadPinnedMemberIdentityIds(conversationId);
  return wrapsViolatePinnedMemberSet(wraps, pinned, [localIdentityId]);
}
